import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../config/config";
import { getToken } from "../../utils/localStorageMethods";
import i18n from "./i18n";
import { translateManyClient } from "../../utils/ItClient";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

export const userSlice = createApi({
  reducerPath: "userSlice",
  baseQuery: fetchBaseQuery({
    baseUrl: `${apiUrl}/user`,
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = getToken(); // Get token from cookies
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
        headers.set("ngrok-skip-browser-warning", "true");
      }
      return headers;
    },
  }),
  tagTypes: ["users"],
  endpoints: (builder) => ({
    getAllUsers: builder.query({
      query: ({ page, limit, role, email, status, exclude = false }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (role) params.append("role", role);
        if (email) params.append("email", email);
        if (exclude) params.append("exclude", exclude);
        if (status) params.append("status", status);

        return `?${params.toString()}`;
      },

      // inside getReservedVehicles endpoint:
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const userData = data?.data?.users ?? [];
          if (!Array.isArray(userData) || userData.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const user of userData) {
            if (user.full_name) bucket.push(user.full_name);
            if (user.email) bucket.push(user.email);
            if (user.street) bucket.push(user.street);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            userSlice.util.updateQueryData("getAllUsers", arg, (draft) => {
              const arr = draft?.data?.users ?? [];
              arr.forEach((user) => {
                user.full_name = pick(user.full_name);
                user.email = pick(user.email);
                user.street = pick(user.street);
              });
            }),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["users"],
    }),
    getUserDetails: builder.query({
      query: (id) => id,
      providesTags: ["users"],
    }),
    createUser: builder.mutation({
      query: (data) => ({
        url: "register",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["users"],
    }),
    updateUser: builder.mutation({
      query: ({ data, id }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["users"],
    }),
    deleteSingleUser: builder.mutation({
      query: ({ id, reason }) => ({
        url: `${id}`,
        method: "Delete",
        body: { reason },
      }),
      invalidatesTags: ["users"],
    }),
    deleteMultiUser: builder.mutation({
      query: (data) => ({
        url: "bulk-delete",
        method: "Post",
        body: data,
      }),
      invalidatesTags: ["users"],
    }),
    permanentDeleteUser: builder.mutation({
      query: ({ id, reason }) => ({
        url: `${id}/permanent`,
        method: "Delete",
        body: { reason },
      }),
      invalidatesTags: ["users"],
    }),
    bulkPermanentDeleteUser: builder.mutation({
      query: ({ userIds, reason }) => ({
        url: "bulk-permanent-delete",
        method: "Post",
        body: { userIds, reason },
      }),
      invalidatesTags: ["users"],
    }),
    updateProfile: builder.mutation({
      query: ({ data, id }) => ({
        url: `${id}`,
        method: "PUT",
        body: data,
      }),
    }),

    activateMultiUser: builder.mutation({
      query: (data) => ({
        url: "active",
        method: "Post",
        body: data,
      }),
      invalidatesTags: ["users"],
    }),
  }),
});

export const {
  useGetAllUsersQuery,
  useGetUserDetailsQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateProfileMutation,
  useDeleteSingleUserMutation,
  useDeleteMultiUserMutation,
  usePermanentDeleteUserMutation,
  useBulkPermanentDeleteUserMutation,
  useActivateMultiUserMutation,
} = userSlice;
